import { CartItem } from '../types';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Button } from './ui/button';
import { X, Minus, Plus } from 'lucide-react@0.487.0';
import { getOptimizedImageUrl } from '../utils/imageOptimization';

interface CartSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cartItems: CartItem[];
  onUpdateQuantity: (itemKey: string, delta: number) => void;
  onRemoveItem: (itemKey: string) => void;
  onCheckout: () => void;
  shippingPrice: number;
}

export function CartSheet({ 
  open, 
  onOpenChange, 
  cartItems, 
  onUpdateQuantity, 
  onRemoveItem,
  onCheckout,
  shippingPrice
}: CartSheetProps) {
  const subtotal = cartItems.reduce((sum, item) => sum + item.selectedSize.price * item.quantity, 0);
  const total = subtotal + shippingPrice;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg bg-white p-0 border-l border-black/5">
        <div className="flex flex-col h-full">
          <SheetHeader className="p-4 md:p-8 border-b border-black/5">
            <div className="flex items-center justify-between">
              <SheetTitle className="tracking-tight">Cart</SheetTitle>
              <button onClick={() => onOpenChange(false)} className="hover:opacity-60">
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
            <SheetDescription className="sr-only">
              Overview of items in your cart
            </SheetDescription>
          </SheetHeader>

          <div className="relative flex-1 overflow-y-auto p-4 md:p-8">
            {cartItems.length === 0 ? (
              <div className="text-center py-16 text-neutral-500">
                <p className="tracking-wide">Cart is empty</p>
              </div>
            ) : (
              <div className="space-y-6 md:space-y-8">
                {cartItems.map((item) => {
                  const itemKey = `${item.photo.id}-${item.selectedSize.id}`;
                  return (
                    <div key={itemKey} className="border-b border-neutral-100 pb-6 md:pb-8">
                      <div className="flex gap-4 md:gap-6">
                        <div className="w-28 h-36 md:w-36 md:h-[180px] bg-neutral-100 flex-shrink-0">
                          <img 
                            src={getOptimizedImageUrl(item.photo.imageUrl, 'thumbnail', item.photo.thumbnailUrl)} 
                            alt={item.photo.title}
                            className="w-full h-full object-contain saturate-50"
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between mb-2 gap-2">
                            <h4 className="text-sm tracking-wide truncate">{item.photo.title}</h4>
                            <button
                              onClick={() => onRemoveItem(itemKey)}
                              className="text-neutral-400 hover:text-black transition-colors flex-shrink-0"
                            >
                              <X className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                          </div>
                          <p className="text-xs text-neutral-500 mb-3 tracking-wide">
                            {item.selectedSize.name} · {item.selectedSize.available} available
                          </p>
                          
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 md:gap-3 border border-neutral-200">
                              <button 
                                className="p-1.5 md:p-2 hover:bg-neutral-50 disabled:opacity-30"
                                onClick={() => onUpdateQuantity(itemKey, -1)}
                                disabled={item.quantity <= 1}
                              >
                                <Minus className="h-3 w-3" strokeWidth={1.5} />
                              </button>
                              <span className="text-sm w-6 md:w-8 text-center">{item.quantity}</span>
                              <button 
                                className="p-1.5 md:p-2 hover:bg-neutral-50 disabled:opacity-30"
                                onClick={() => onUpdateQuantity(itemKey, 1)}
                                disabled={item.quantity >= item.selectedSize.available}
                              >
                                <Plus className="h-3 w-3" strokeWidth={1.5} />
                              </button>
                            </div>
                            <p className="text-sm tracking-wide whitespace-nowrap">
                              €{item.selectedSize.price * item.quantity}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {cartItems.length > 0 && (
            <div className="p-4 md:p-8 border-t border-black/5 space-y-4 md:space-y-6">
              <div className="space-y-2 md:space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500 tracking-wide">Subtotal</span>
                  <span className="tracking-wide">€{subtotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500 tracking-wide">Shipping</span>
                  <span className="tracking-wide text-green-600">FREE</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-neutral-100">
                  <span className="tracking-wide">Total</span>
                  <span className="tracking-wide">€{total}</span>
                </div>
              </div>

              <Button 
                className="w-full bg-black text-white hover:bg-neutral-800 h-12" 
                onClick={onCheckout}
              >
                Continue
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default CartSheet;